import { useLocalization } from "@fluent/react"
import { Box, ButtonBase, Divider, IconButton, Paper, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import { DepartmentCodeBadge } from "../../administration/companyDepartments/components/DepartmentCodeBadge"
import { OperationalDashboardAreaContentOverviewDto, OperationalDashboardAreaOverviewDto } from "../operationalDashboards"
import type { UpdateOperationalDashboardAreaContentPriorityRequest } from "../operationalDashboards"
import { AreaContents, AreaDecision, AreaDiscussionAction, areaRating, AreaStatusChip } from "./OperationalDashboardAreaPanel"
import { AreaRatingSelector, AreaRatingValue } from "./AreaRatingSelector"
import { OpenMeetingMinutesButton } from "./OpenMeetingMinutesButton"

type AreaListItemProps = {
    area: OperationalDashboardAreaOverviewDto
    selected: boolean
    editMode: boolean
    hasMeeting: boolean
    inMeeting: boolean
    meetingMinutesId: string | null
    onSelect: () => void
    onEdit: () => void
    onChangeRating?: (rating: AreaRatingValue) => void
    ratingSaving?: boolean
    ratingBusy?: boolean
    showAreaStatus?: boolean
}

export const AreaListItem = ({ area, selected, editMode, hasMeeting, inMeeting, meetingMinutesId, onSelect, onEdit,
    onChangeRating, ratingSaving, ratingBusy, showAreaStatus = true }: AreaListItemProps) => {
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)

    return (
        <Box sx={theme => ({
            display: "flex",
            borderBottom: 1, borderColor: "divider", borderLeft: "3px solid",
            borderLeftColor: area.departmentColor?.trim() || (selected ? theme.palette.primary.main : "transparent"),
            bgcolor: selected ? alpha(theme.palette.primary.main, 0.09) : "background.paper",
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        })}>
            <ButtonBase component="button" type="button" aria-pressed={selected} onClick={onSelect}
                sx={{ display: "block", flex: 1, minWidth: 0, textAlign: "left", p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                        {area.departmentId != null && area.departmentCode != null && (
                            <DepartmentCodeBadge code={area.departmentCode} color={area.departmentColor ?? undefined} />
                        )}
                        <Typography fontWeight={600} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{area.name}</Typography>
                    </Stack>
                    {showAreaStatus && <AreaStatusChip rating={areaRating(area, hasMeeting)} />}
                </Stack>
                {area.description?.trim() && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                        {area.description}
                    </Typography>
                )}
            </ButtonBase>
            <Box sx={{ alignSelf: "flex-start", mt: 1.5, mr: editMode ? 0 : 1, display: "flex", alignItems: "center", gap: 0.25 }}>
               
                <OpenMeetingMinutesButton meetingMinutesId={meetingMinutesId} agendaItemId={area.meetingAgendaItemId} iconOnly />
            </Box>
            {editMode && (
                <IconButton size="small" onClick={onEdit} aria-label={label("tile-settings", "Ustawienia kafelka")}
                    sx={{ alignSelf: "flex-start", mt: 1.5, mr: 1 }}>
                    <SettingsOutlinedIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    )
}

type AreaInspectorProps = {
    area: OperationalDashboardAreaOverviewDto
    showDiscussionStatus: boolean
    hasMeeting: boolean
    inMeeting: boolean
    meetingMinutesId: string | null
    onEditContent?: (content: OperationalDashboardAreaContentOverviewDto) => void
    onChangePriority?: (content: OperationalDashboardAreaContentOverviewDto, value: UpdateOperationalDashboardAreaContentPriorityRequest["priority"]) => void
    priorityBusy?: boolean
    showPriorityDots?: boolean
    onViewContentHistory?: (content: OperationalDashboardAreaContentOverviewDto) => void
    onViewAgendaHistory?: () => void
    onEditSummary?: () => void
    onToggleDiscussed?: () => void
    discussing?: boolean
    discussionBusy?: boolean
    onChangeRating?: (rating: AreaRatingValue) => void
    ratingSaving?: boolean
    ratingBusy?: boolean
    showAreaStatus?: boolean
}

export const AreaInspector = ({ area, showDiscussionStatus, hasMeeting, inMeeting, meetingMinutesId, onEditContent, onViewContentHistory, onViewAgendaHistory,
    onEditSummary, onToggleDiscussed, discussing, discussionBusy, onChangeRating, ratingSaving, ratingBusy,
    onChangePriority, priorityBusy, showPriorityDots = true, showAreaStatus = true }: AreaInspectorProps) => {
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)

    return (
        <Paper component="aside" variant="outlined" aria-label={`${label("area-overview", "Przegląd obszaru")}: ${area.name}`}
            sx={{
                p: { xs: 2, md: 3 }, borderRadius: 2, backgroundImage: "none",
                bgcolor: "background.paper",
                position: { lg: "sticky" }, top: { lg: 16 }, minWidth: 0,
            }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} sx={{ minWidth: 0 }}>
                {area.departmentId != null && area.departmentCode != null && (
                    <DepartmentCodeBadge code={area.departmentCode} color={area.departmentColor ?? undefined} />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography component="h2" variant="h5" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                        {area.name}
                    </Typography>
                    {area.description?.trim() && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                            {area.description}
                        </Typography>
                    )}
                </Box>
                {showAreaStatus && (
                    <AreaRatingSelector
                        rating={areaRating(area, hasMeeting)}
                        onChange={value => onChangeRating?.(value)}
                        disabled={!inMeeting || area.meetingAgendaItemId == null || !onChangeRating || ratingBusy}
                        saving={ratingSaving} />
                )}
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                {label("area-owner", "Właściciel obszaru")}: {area.responsibleName || l10n.getString("unassigned", null, "_Nie przypisano")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <AreaContents contents={area.contents} large onEditContent={onEditContent}
                onChangePriority={onChangePriority} priorityBusy={priorityBusy} showPriorityDots={showPriorityDots}
                onViewContentHistory={onViewContentHistory} />
            <Divider sx={{ my: 2 }} />
            <AreaDecision summary={area.summary} large
                onHistory={onViewAgendaHistory}
                onEdit={area.meetingAgendaItemId != null ? onEditSummary : undefined} />
            {showDiscussionStatus && (
                <AreaDiscussionAction canDiscuss={inMeeting && area.meetingAgendaItemId != null}
                    discussed={area.discussionStatus?.toUpperCase() === "DISCUSSED"}
                    onDiscuss={onToggleDiscussed} discussing={discussing} discussionBusy={discussionBusy} />
            )}
            <Box sx={{ mt: 2 }}>
                <OpenMeetingMinutesButton meetingMinutesId={meetingMinutesId} agendaItemId={area.meetingAgendaItemId} />
            </Box>
        </Paper>
    )
}
